import { Injectable, OnDestroy } from '@angular/core';
import { Config, ConfigurationService } from '@app/shared/services/api/configuration.service';
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { map, mergeMap, startWith, takeUntil, tap } from 'rxjs/operators';
import { EfficiencyThreshold } from '../models/thresholds/efficiency-threshold';

@Injectable({
  providedIn: 'root',
})
export class EfficiencyTresholdService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private efficientTresholdStore: BehaviorSubject<Config> = new BehaviorSubject<Config>({} as Config);
  private inefficientTresholdStore: BehaviorSubject<Config> = new BehaviorSubject<Config>({} as Config);

  private getEfficientTreshold = this.config.getByKey('efficientTreshold');
  private getInefficientTreshold = this.config.getByKey('inefficientTreshold');

  public treshold: Observable<EfficiencyThreshold> = combineLatest([
    this.efficientTresholdStore.asObservable().pipe(startWith()),
    this.inefficientTresholdStore.asObservable().pipe(startWith()),
  ]).pipe(
    map(([efficient, inefficient]: [Config, Config]) => ({
      efficientTreshold: Number(efficient ? efficient.configValue : 0),
      inefficientTreshold: Number(inefficient ? inefficient.configValue : 67),
    })),
    takeUntil(this.unsubscribe$),
  );

  constructor(private config: ConfigurationService) {
    forkJoin([this.getEfficientTreshold, this.getInefficientTreshold])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([efficient, inefficient]: [Config, Config]) => {
        this.efficientTresholdStore.next(efficient);
        this.inefficientTresholdStore.next(inefficient);
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public updateEfficiencyTreshold(efficient: string, inefficient: string): Observable<EfficiencyThreshold> {
    return combineLatest([this.updateEfficientTreshold(efficient), this.updateInefficientTreshold(inefficient)]).pipe(
      mergeMap(() => this.treshold),
      takeUntil(this.unsubscribe$),
    );
  }

  private updateEfficientTreshold(value: string): Observable<Config> {
    return this.updateEfficientConfiguration(value).pipe(tap((efficient: Config) => this.efficientTresholdStore.next(efficient)));
  }
  private updateInefficientTreshold(value: string): Observable<Config> {
    return this.updateInefficientConfiguration(value).pipe(tap((inefficient: Config) => this.inefficientTresholdStore.next(inefficient)));
  }

  private updateEfficientConfiguration(value: string): Observable<Config> {
    return of(value).pipe(
      map(
        (efficient: string): Config => ({
          ...this.efficientTresholdStore.getValue(),
          configValue: efficient,
        }),
      ),
      mergeMap((newConfig: Config) => this.config.updateConfiguration(newConfig)),
    );
  }

  private updateInefficientConfiguration(value: string): Observable<Config> {
    return of(value).pipe(
      map(
        (inefficient: string): Config => ({
          ...this.inefficientTresholdStore.getValue(),
          configValue: inefficient,
        }),
      ),
      mergeMap((newConfig: Config) => this.config.updateConfiguration(newConfig)),
    );
  }
}
